/* ---------------------------------------------------------------------------
 * API client — the single boundary between the WarriorLab web app and the
 * Fastify backend. Every screen calls through here. In dev, Vite proxies /api
 * to http://localhost:8788 (see vite.config.ts). Sessions are httpOnly
 * cookies, so we always send credentials and never touch tokens in JS.
 * Company context comes from the URL — there is no ambient tenant header.
 * ------------------------------------------------------------------------- */
import type {
  UserKind,
  CompanyRole,
  RunState,
  AgentKind,
  SuggestionKind,
  SuggestionStatus,
  KnowledgeKind,
  KnowledgeOrigin,
  ConnectorKind,
  ConnectorStatus,
  ContextFolder,
  Cadence,
  Visibility,
} from '@warriorlab/shared';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const BASE = '/api';

/** Friendlier copy for common statuses when the server sends no message of its own. */
function fallbackMessage(status: number, statusText: string): string {
  if (status === 401) return 'Your session has expired. Sign in again.';
  if (status === 403) return "You don't have access to do that.";
  if (status === 404) return 'Not found.';
  if (status === 429) return 'Too many requests. Try again shortly.';
  if (status === 500 || status === 502 || status === 503) return 'The server had a problem. Try again.';
  return statusText;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string>),
  };
  // Only declare a JSON body when there is one — Fastify rejects an empty body
  // sent with content-type: application/json (breaks bodyless POST/DELETE).
  if (init.body !== undefined) headers['content-type'] = 'application/json';

  const res = await fetch(`${BASE}${path}`, { credentials: 'include', ...init, headers });
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) throw new ApiError(data?.message ?? fallbackMessage(res.status, res.statusText), res.status, data?.code);
  return data as T;
}

/* ------------------------------- shapes ------------------------------- */

export interface SessionUser {
  id: string;
  name: string | null;
  email: string;
  kind: UserKind;
}
export interface SessionCompany {
  id: string;
  slug: string;
  name: string;
  role: CompanyRole;
}
export interface SessionPayload {
  user: SessionUser;
  companies: SessionCompany[];
}

export interface CompanySummary {
  id: string;
  slug: string;
  name: string;
  status: string;
  websiteUrl: string | null;
  logoUrl: string | null;
  workspaceCount: number;
  memberCount: number;
  createdAt: string;
}
export interface WorkspaceSummary {
  id: string;
  slug: string;
  name: string;
  status: string;
  projectCount: number;
  contextCount: number;
  pendingSuggestions: number;
  lastRun: { state: RunState; at: string } | null;
}
export interface CompanyDetail {
  id: string;
  slug: string;
  name: string;
  status: string;
  websiteUrl: string | null;
  logoUrl: string | null;
  createdAt: string;
  workspaces: WorkspaceSummary[];
}

export interface Member {
  id: string;
  role: CompanyRole;
  user: { id: string; email: string; name: string | null; kind: UserKind };
}

/** One row in the platform-wide user directory (staff-only). */
export interface PlatformUser {
  id: string;
  email: string;
  name: string | null;
  kind: UserKind;
  createdAt: string;
  memberships: { role: CompanyRole; company: { id: string; name: string; slug: string } }[];
}

export interface ProjectSummary {
  id: string;
  slug: string;
  name: string;
  goal: string | null;
  status: string;
  runCount: number;
  deliverableCount: number;
  pendingSuggestions: number;
  scheduleCadence: Cadence | null;
  nextRunAt: string | null;
  agentNames: string[];
  lastRun: { id: string; state: RunState; at: string } | null;
}
export interface WorkspaceDetail {
  id: string;
  slug: string;
  name: string;
  status: string;
  counts: { contextItems: number; knowledgeEntries: number; suggestions: number };
  projects: ProjectSummary[];
}

export interface ProjectAgent {
  id: string;
  slug: string;
  name: string;
  kind: AgentKind;
  description: string | null;
}
export interface ProjectDetail {
  id: string;
  slug: string;
  name: string;
  goal: string | null;
  status: string;
  scheduleAgentId: string | null;
  scheduleCadence: Cadence | null;
  workspace: { id: string; slug: string; name: string; companyId: string };
  agents: ProjectAgent[];
}

export interface Agent {
  id: string;
  slug: string;
  name: string;
  kind: AgentKind;
  description: string | null;
  model: string | null;
  tools: string[];
  body: string;
  published: boolean;
  updatedAt: string;
  /** Present on the list endpoint (Prisma _count include). */
  _count?: { enabledBy: number; runs: number };
}

export interface Run {
  id: string;
  title: string | null;
  state: RunState;
  agentId: string;
  projectId: string;
  workspaceId: string;
  companyId: string;
  input: string | null;
  reason: string | null;
  scheduled: boolean;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  /** Included by the API on list/detail for display. */
  agent?: { name: string; slug: string };
}
export interface RunEventItem {
  seq: number;
  type: string;
  payload: unknown;
  createdAt: string;
}
export type RunDetail = Run & { events: RunEventItem[] };

export interface Deliverable {
  id: string;
  path: string;
  title: string;
  visibility: Visibility;
  version: number;
  lastRunId: string | null;
  updatedAt: string;
  createdAt: string;
}
export interface DeliverableRender {
  id: string;
  variant: 'client' | 'internal';
  sample: boolean;
  renderedAt: string;
}
export type DeliverableDetail = Deliverable & {
  projectId: string;
  workspaceId: string;
  companyId: string;
  content: string;
  renders: DeliverableRender[];
};

export interface Suggestion {
  id: string;
  workspaceId: string;
  projectId: string | null;
  runId: string | null;
  kind: SuggestionKind;
  title: string;
  body: string;
  proposedKnowledge: string | null;
  confidence: number;
  /** Context fileNames cited by the agent (JSON — treat defensively). */
  sources: unknown;
  status: SuggestionStatus;
  reviewedAt: string | null;
  feedback: string | null;
  createdAt: string;
}

/** Four-level knowledge cascade. Local union — @warriorlab/shared's KnowledgeScope
 * predates COMPANY/PROJECT and this workspace must not modify shared. */
export type KnowledgeEntryScope = 'GLOBAL' | 'COMPANY' | 'WORKSPACE' | 'PROJECT';

export interface KnowledgeEntry {
  id: string;
  scope: KnowledgeEntryScope;
  kind: KnowledgeKind;
  title: string;
  body: string;
  tags: string[];
  origin: KnowledgeOrigin;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

/** One row of the company-wide knowledge base browser (GET /companies/:id/knowledge/base). */
export interface KBEntry {
  id: string;
  scope: KnowledgeEntryScope;
  kind: KnowledgeKind;
  title: string;
  body: string;
  tags: string[];
  origin: KnowledgeOrigin;
  createdAt: string;
  updatedAt: string;
  location: { label: string; workspaceSlug?: string; projectSlug?: string };
}

export interface PersonInvolvement {
  workspaceId: string;
  workspaceSlug: string;
  workspaceName: string;
  role: string | null;
}
export interface Person {
  id: string;
  name: string;
  title: string | null;
  email: string | null;
  notes: string | null;
  createdAt: string;
  involvements: PersonInvolvement[];
}
/** One workspace's cast list (GET /workspaces/:id/people). */
export interface WorkspacePerson {
  personId: string;
  name: string;
  title: string | null;
  email: string | null;
  role: string | null;
}

export interface ContextItemSummary {
  id: string;
  folder: ContextFolder;
  fileName: string;
  title: string;
  tags: string[];
  kind: ConnectorKind | null;
  url: string | null;
  connectorId: string | null;
  ingestedAt: string;
  updatedAt: string;
  /** Present when the item was created by file upload — original is downloadable. */
  file?: { id: string; originalName: string; size: number } | null;
  /** Source metadata; meta.routing carries auto-router state (pending/auto/manual). */
  meta?: Record<string, unknown> | null;
}
export type ContextItemDetail = ContextItemSummary & { workspaceId: string; content: string };

export interface Connector {
  id: string;
  workspaceId: string;
  kind: ConnectorKind;
  name: string;
  /** A note for the agents about what this source is — included in every run. */
  contextNote: string | null;
  status: ConnectorStatus;
  destFolder: ContextFolder;
  syncCadence: Cadence | null;
  nextSyncAt: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  /** Shared-account intake: auto-route newly synced items by routing rules. */
  routeOnSync: boolean;
}
export type ConnectorConfig = Record<string, unknown>;

export interface RoutingRule {
  id: string;
  kind: 'KEYWORD' | 'EMAIL';
  value: string;
}

/** One row of a connector's per-file activity log. */
export interface SyncActivityEvent {
  id: string;
  action: 'CREATED' | 'UPDATED' | 'DISTRIBUTED' | 'PENDING';
  contextItemId: string | null;
  fileName: string;
  title: string;
  at: string;
  /** Rule hits / manual flag / routing-instruction outcome. */
  detail: { via?: 'rules' | 'prompt' | 'manual'; hits?: string[]; instruction?: string; error?: string; routed?: number } | null;
  /** Destination workspace for DISTRIBUTED events. */
  workspace: { id: string; name: string; slug: string; company: { name: string; slug: string } } | null;
}
export interface WorkspaceRef {
  id: string;
  name: string;
  slug: string;
  company: { id: string; name: string; slug: string };
}
export interface RouteSuggestion {
  workspaceId: string;
  hits: string[];
  workspace: { id: string; name: string; company: { id: string; name: string } };
}

/* staff operations rollups */
export interface OpsWorkspaceRef {
  id?: string;
  slug: string;
  name: string;
  companySlug: string;
  companyName: string;
}
export interface OpsConnector {
  id: string;
  kind: ConnectorKind;
  name: string;
  contextNote: string | null;
  status: ConnectorStatus;
  destFolder: ContextFolder;
  syncCadence: Cadence | null;
  nextSyncAt: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  routeOnSync: boolean;
  itemCount: number;
  workspace: OpsWorkspaceRef;
}
export interface OpsWorkflow {
  projectId: string;
  projectSlug: string;
  projectName: string;
  status: string;
  cadence: Cadence | null;
  nextRunAt: string | null;
  agent: { id: string; name: string; slug: string } | null;
  lastScheduledRun: { id: string; state: RunState; at: string } | null;
  workspace: OpsWorkspaceRef;
}

export interface ClaudeCredentialStatus {
  connected: boolean;
  updatedAt: string | null;
}

/* ------------------------------- methods ------------------------------- */

export const api = {
  /* auth */
  login: (email: string, password: string) =>
    request<SessionPayload>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  me: () => request<SessionPayload>('/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  /* companies */
  listCompanies: () => request<CompanySummary[]>('/companies'),
  createCompany: (name: string) =>
    request<CompanySummary>('/companies', { method: 'POST', body: JSON.stringify({ name }) }),
  getCompany: (companyId: string) => request<CompanyDetail>(`/companies/${companyId}`),
  updateCompany: (
    companyId: string,
    body: { name?: string; status?: 'active' | 'paused' | 'archived'; websiteUrl?: string | null; logoUrl?: string | null },
  ) =>
    request<CompanyDetail>(`/companies/${companyId}`, { method: 'PATCH', body: JSON.stringify(body) }),

  /* members */
  listMembers: (companyId: string) => request<Member[]>(`/companies/${companyId}/members`),
  addMember: (companyId: string, body: { email: string; name?: string; password?: string; role: CompanyRole }) =>
    request<Member>(`/companies/${companyId}/members`, { method: 'POST', body: JSON.stringify(body) }),
  removeMember: (companyId: string, membershipId: string) =>
    request<{ ok: true }>(`/companies/${companyId}/members/${membershipId}`, { method: 'DELETE' }),
  resetMemberPassword: (companyId: string, membershipId: string, newPassword: string) =>
    request<{ ok: true }>(`/companies/${companyId}/members/${membershipId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    }),

  /* workspaces */
  createWorkspace: (companyId: string, name: string) =>
    request<WorkspaceSummary>(`/companies/${companyId}/workspaces`, { method: 'POST', body: JSON.stringify({ name }) }),
  getWorkspace: (workspaceId: string) => request<WorkspaceDetail>(`/workspaces/${workspaceId}`),
  updateWorkspace: (workspaceId: string, body: { name?: string; status?: 'active' | 'paused' | 'archived' }) =>
    request<WorkspaceSummary>(`/workspaces/${workspaceId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  updateProject: (projectId: string, body: { name?: string; goal?: string; status?: 'active' | 'paused' | 'archived' }) =>
    request<ProjectDetail>(`/projects/${projectId}`, { method: 'PATCH', body: JSON.stringify(body) }),

  /* projects */
  createProject: (workspaceId: string, body: { name: string; goal?: string }) =>
    request<ProjectSummary>(`/workspaces/${workspaceId}/projects`, { method: 'POST', body: JSON.stringify(body) }),
  getProject: (projectId: string) => request<ProjectDetail>(`/projects/${projectId}`),

  /* agents (staff) */
  listAgents: () => request<Agent[]>('/agents'),
  createAgent: (body: Partial<Agent> & { name: string; body: string }) =>
    request<Agent>('/agents', { method: 'POST', body: JSON.stringify(body) }),
  getAgent: (agentId: string) => request<Agent>(`/agents/${agentId}`),
  updateAgent: (agentId: string, body: Partial<Agent>) =>
    request<Agent>(`/agents/${agentId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteAgent: (agentId: string) => request<{ ok: true }>(`/agents/${agentId}`, { method: 'DELETE' }),

  /* project wiring (staff) */
  setProjectAgents: (projectId: string, agentIds: string[]) =>
    request<{ ok: true }>(`/projects/${projectId}/agents`, { method: 'PUT', body: JSON.stringify({ agentIds }) }),
  setProjectSchedule: (projectId: string, body: { agentId: string | null; cadence: Cadence | null }) =>
    request<unknown>(`/projects/${projectId}/schedule`, { method: 'PUT', body: JSON.stringify(body) }),

  /* runs */
  startRun: (projectId: string, body: { agentId: string; input?: string }) =>
    request<Run>(`/projects/${projectId}/runs`, { method: 'POST', body: JSON.stringify(body) }),
  listRuns: (projectId: string) => request<Run[]>(`/projects/${projectId}/runs`),
  getRun: (runId: string) => request<RunDetail>(`/runs/${runId}`),
  cancelRun: (runId: string) => request<{ ok: true }>(`/runs/${runId}/cancel`, { method: 'POST' }),

  /* deliverables */
  listDeliverables: (projectId: string) => request<Deliverable[]>(`/projects/${projectId}/deliverables`),
  getDeliverable: (deliverableId: string) => request<DeliverableDetail>(`/deliverables/${deliverableId}`),
  renderDeliverable: (deliverableId: string, body: { variant?: 'client' | 'internal'; sample?: boolean }) =>
    request<{ queued: boolean; variant: string }>(`/deliverables/${deliverableId}/render`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Cookie-authed PDF download — usable as a plain anchor href. */
  renderPdfUrl: (deliverableId: string, renderId: string) =>
    `${BASE}/deliverables/${deliverableId}/renders/${renderId}.pdf`,

  /* suggestions (review loop) */
  getPendingReviewCount: () => request<{ count: number }>(`/suggestions/pending-count`),
  listSuggestions: (workspaceId: string, status?: SuggestionStatus) =>
    request<Suggestion[]>(`/workspaces/${workspaceId}/suggestions${status ? `?status=${status}` : ''}`),
  updateSuggestion: (suggestionId: string, body: { title?: string; body?: string; proposedKnowledge?: string }) =>
    request<Suggestion>(`/suggestions/${suggestionId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  approveSuggestion: (suggestionId: string) =>
    request<Suggestion>(`/suggestions/${suggestionId}/approve`, { method: 'POST' }),
  declineSuggestion: (suggestionId: string, feedback?: string) =>
    request<Suggestion>(`/suggestions/${suggestionId}/decline`, {
      method: 'POST',
      body: JSON.stringify(feedback ? { feedback } : {}),
    }),

  /* knowledge — the cascade: company → workspace → project */
  listKnowledge: (workspaceId: string, archived = false) =>
    request<KnowledgeEntry[]>(`/workspaces/${workspaceId}/knowledge${archived ? '?archived=true' : ''}`),
  createKnowledge: (workspaceId: string, body: { kind: KnowledgeKind; title: string; body: string; tags?: string[] }) =>
    request<KnowledgeEntry>(`/workspaces/${workspaceId}/knowledge`, { method: 'POST', body: JSON.stringify(body) }),
  listCompanyKnowledge: (companyId: string, archived = false) =>
    request<KnowledgeEntry[]>(`/companies/${companyId}/knowledge${archived ? '?archived=true' : ''}`),
  createCompanyLearning: (companyId: string, body: { title: string; body: string; tags?: string[] }) =>
    request<KnowledgeEntry>(`/companies/${companyId}/knowledge`, { method: 'POST', body: JSON.stringify(body) }),
  listProjectKnowledge: (projectId: string, archived = false) =>
    request<KnowledgeEntry[]>(`/projects/${projectId}/knowledge${archived ? '?archived=true' : ''}`),
  createProjectLearning: (projectId: string, body: { title: string; body: string; tags?: string[] }) =>
    request<KnowledgeEntry>(`/projects/${projectId}/knowledge`, { method: 'POST', body: JSON.stringify(body) }),
  updateKnowledge: (entryId: string, body: { title?: string; body?: string; tags?: string[]; archived?: boolean }) =>
    request<KnowledgeEntry>(`/knowledge/${entryId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  /** The whole knowledge cascade under one company, newest first. */
  getKnowledgeBase: (companyId: string) => request<KBEntry[]>(`/companies/${companyId}/knowledge/base`),

  /* people register */
  listPeople: (companyId: string) => request<Person[]>(`/companies/${companyId}/people`),
  createPerson: (companyId: string, body: { name: string; title?: string | null; email?: string | null; notes?: string | null }) =>
    request<Person>(`/companies/${companyId}/people`, { method: 'POST', body: JSON.stringify(body) }),
  updatePerson: (personId: string, body: { name?: string; title?: string | null; email?: string | null; notes?: string | null }) =>
    request<Person>(`/people/${personId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deletePerson: (personId: string) => request<{ ok: true }>(`/people/${personId}`, { method: 'DELETE' }),
  setPersonWorkspaces: (personId: string, involvements: { workspaceId: string; role?: string | null }[]) =>
    request<Person>(`/people/${personId}/workspaces`, { method: 'PUT', body: JSON.stringify({ involvements }) }),
  listWorkspacePeople: (workspaceId: string) => request<WorkspacePerson[]>(`/workspaces/${workspaceId}/people`),

  /* context (staff-only) */
  listContext: (workspaceId: string) => request<ContextItemSummary[]>(`/workspaces/${workspaceId}/context`),
  getContextItem: (itemId: string) => request<ContextItemDetail>(`/context/${itemId}`),
  createContextItem: (workspaceId: string, body: { title: string; content: string; folder: ContextFolder }) =>
    request<ContextItemDetail>(`/workspaces/${workspaceId}/context`, { method: 'POST', body: JSON.stringify(body) }),
  uploadContextFile: async (workspaceId: string, file: File, folder: ContextFolder, title?: string) => {
    const form = new FormData();
    // Scalar fields must be appended before the file part so busboy sees them
    // when the file stream is consumed.
    form.append('folder', folder);
    if (title) form.append('title', title);
    form.append('file', file);
    const res = await fetch(`${BASE}/workspaces/${workspaceId}/context/upload`, {
      method: 'POST',
      credentials: 'include',
      body: form, // browser sets the multipart boundary — no content-type here
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => null)) as { message?: string; code?: string } | null;
      throw new ApiError(data?.message ?? fallbackMessage(res.status, res.statusText), res.status, data?.code);
    }
    return (await res.json()) as ContextItemSummary;
  },
  contextFileUrl: (itemId: string) => `${BASE}/context/${itemId}/file`,
  updateContextItem: (
    itemId: string,
    body: { title?: string; folder?: ContextFolder; tags?: string[] },
  ) => request<ContextItemDetail>(`/context/${itemId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteContextItem: (itemId: string) => request<{ ok: true }>(`/context/${itemId}`, { method: 'DELETE' }),

  /* connectors (staff-only) */
  listConnectors: (workspaceId: string) => request<Connector[]>(`/workspaces/${workspaceId}/connectors`),
  createConnector: (
    workspaceId: string,
    body: {
      kind: ConnectorKind;
      name: string;
      config: ConnectorConfig;
      contextNote?: string | null;
      destFolder?: ContextFolder;
      syncCadence?: Cadence | null;
      routeOnSync?: boolean;
    },
  ) => request<Connector>(`/workspaces/${workspaceId}/connectors`, { method: 'POST', body: JSON.stringify(body) }),
  updateConnector: (
    connectorId: string,
    body: {
      name?: string;
      config?: ConnectorConfig;
      contextNote?: string | null;
      destFolder?: ContextFolder;
      syncCadence?: Cadence | null;
      routeOnSync?: boolean;
    },
  ) => request<Connector>(`/connectors/${connectorId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  syncConnector: (connectorId: string, prompt?: string) =>
    request<{ ok: true }>(`/connectors/${connectorId}/sync`, {
      method: 'POST',
      body: JSON.stringify(prompt?.trim() ? { prompt: prompt.trim() } : {}),
    }),
  connectorActivity: (connectorId: string) =>
    request<SyncActivityEvent[]>(`/connectors/${connectorId}/activity`),

  /* meeting routing (staff-only) */
  ensureIntakeWorkspace: () =>
    request<{ workspaceId: string; workspaceSlug: string; companySlug: string }>(`/operations/intake-workspace`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  listAllWorkspaces: () => request<WorkspaceRef[]>(`/workspaces/all`),
  listRoutingRules: (workspaceId: string) => request<RoutingRule[]>(`/workspaces/${workspaceId}/routing-rules`),
  saveRoutingRules: (workspaceId: string, rules: { kind: 'KEYWORD' | 'EMAIL'; value: string }[]) =>
    request<RoutingRule[]>(`/workspaces/${workspaceId}/routing-rules`, { method: 'PUT', body: JSON.stringify({ rules }) }),
  distributeContextItem: (itemId: string, workspaceIds: string[]) =>
    request<ContextItemDetail>(`/context/${itemId}/distribute`, { method: 'POST', body: JSON.stringify({ workspaceIds }) }),
  routeSuggestions: (itemId: string) => request<RouteSuggestion[]>(`/context/${itemId}/route-suggestions`),
  deleteConnector: (connectorId: string) =>
    request<{ ok: true }>(`/connectors/${connectorId}`, { method: 'DELETE' }),

  /* platform (staff-only) */
  getClaudeCredential: () => request<ClaudeCredentialStatus>('/platform/claude-credential'),
  setClaudeCredential: (token: string) =>
    request<{ connected: boolean }>('/platform/claude-credential', { method: 'PUT', body: JSON.stringify({ token }) }),
  deleteClaudeCredential: () =>
    request<{ connected: boolean }>('/platform/claude-credential', { method: 'DELETE' }),
  startClaudeSetup: () =>
    request<{ url: string }>('/platform/claude-credential/setup/start', { method: 'POST' }),
  submitClaudeSetupCode: (code: string) =>
    request<{ connected: boolean }>('/platform/claude-credential/setup/code', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  cancelClaudeSetup: () =>
    request<{ canceled: boolean }>('/platform/claude-credential/setup/cancel', { method: 'POST' }),
  getPlatformCharter: () => request<KnowledgeEntry | null>('/platform/charter'),
  setPlatformCharter: (body: { title: string; body: string }) =>
    request<KnowledgeEntry>('/platform/charter', { method: 'PUT', body: JSON.stringify(body) }),

  /* platform user directory (staff-only) */
  listPlatformUsers: () => request<PlatformUser[]>('/platform/users'),
  createPlatformUser: (body: { email: string; name?: string; password: string; kind: UserKind }) =>
    request<PlatformUser>('/platform/users', { method: 'POST', body: JSON.stringify(body) }),
  updatePlatformUser: (userId: string, body: { name?: string | null; kind?: UserKind }) =>
    request<PlatformUser>(`/platform/users/${userId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  resetPlatformUserPassword: (userId: string, newPassword: string) =>
    request<{ ok: true }>(`/platform/users/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    }),
  deletePlatformUser: (userId: string) =>
    request<{ ok: true }>(`/platform/users/${userId}`, { method: 'DELETE' }),

  /* home overview */
  getOverview: () => request<Overview>('/overview'),

  /* staff operations rollups */
  listOperationsConnectors: () => request<OpsConnector[]>('/operations/connectors'),
  listOperationsWorkflows: () => request<OpsWorkflow[]>('/operations/workflows'),

  /* top-down rollups (any member) */
  listCompanyRuns: (companyId: string) => request<RollupRun[]>(`/companies/${companyId}/runs`),
  listWorkspaceRuns: (workspaceId: string) => request<RollupRun[]>(`/workspaces/${workspaceId}/runs`),
  listCompanyDeliverables: (companyId: string) => request<RollupDeliverable[]>(`/companies/${companyId}/deliverables`),
  listWorkspaceDeliverables: (workspaceId: string) =>
    request<RollupDeliverable[]>(`/workspaces/${workspaceId}/deliverables`),

  /* client portal (the customer-facing surface on each install) */
  getPortalConfig: () => request<PortalConfig>('/portal/config'),
  getPortalOverview: () => request<PortalOverview>('/portal/overview'),
  getPortalDepartment: (companySlug: string) => request<PortalDepartment>(`/portal/departments/${companySlug}`),
  listPortalDeliverables: () => request<PortalDeliverable[]>('/portal/deliverables'),
  getPortalDeliverable: (id: string) => request<PortalDeliverableDetail>(`/portal/deliverables/${id}`),
};

export interface PortalConfig {
  name: string;
  wordmarkA: string;
  wordmarkB: string;
  region: string | null;
  accent: string;
  accentInk: string;
  bg: string;
  bg2: string;
  enabled: boolean;
}
export interface PortalOverview {
  departments: {
    slug: string; name: string; status: string;
    agentCount: number; peopleCount: number;
    lastRun: { title: string; state: string; at: string } | null;
  }[];
  totals: { agents: number; departments: number; people: number; runsThisWeek: number; deliverablesThisMonth: number };
}
export interface PortalDepartment {
  slug: string;
  name: string;
  agents: { id: string; slug: string; name: string; description: string | null; projectId: string }[];
  people: { id: string; name: string; title: string | null }[];
  recentRuns: { id: string; title: string; state: string; agentName: string | null; at: string }[];
}
export interface PortalDeliverable {
  id: string; title: string; format: string; department: string; updatedAt: string; version: number;
}
export interface PortalDeliverableDetail {
  id: string; title: string; content: string; department: string; updatedAt: string; version: number;
}

/* ── top-down rollups ──────────────────────────────────────────────────── */

export interface RollupProjectRef {
  slug: string;
  name: string;
  workspace: { slug: string; name: string };
}
export type RollupRun = Run & { project: RollupProjectRef };
export interface RollupDeliverable {
  id: string;
  path: string;
  title: string;
  visibility: Visibility;
  version: number;
  updatedAt: string;
  project: RollupProjectRef;
}

/* ── home overview ─────────────────────────────────────────────────────── */

export interface OverviewProjectRef {
  slug: string;
  name: string;
  workspaceSlug: string;
  workspaceName: string;
  companySlug: string;
  companyName: string;
}

export interface OverviewRun {
  id: string;
  title: string;
  state: RunState;
  reason: string | null;
  scheduled: boolean;
  agentName: string | null;
  createdAt: string;
  finishedAt: string | null;
  project: OverviewProjectRef;
}

export interface OverviewCompany {
  id: string;
  slug: string;
  name: string;
  status: string;
  websiteUrl: string | null;
  logoUrl: string | null;
  workspaces: { id: string; slug: string; name: string; projects: { id: string; slug: string; name: string }[] }[];
  pendingSuggestions: number;
  lastActivityAt: string | null;
}

export interface Overview {
  companies: OverviewCompany[];
  inFlightRuns: OverviewRun[];
  recentRuns: OverviewRun[];
  pendingSuggestionCount: number;
  pendingSuggestions: {
    id: string;
    kind: SuggestionKind;
    title: string;
    confidence: number;
    createdAt: string;
    workspace: { slug: string; name: string; companySlug: string; companyName: string };
  }[];
  recentDeliverables: {
    id: string;
    path: string;
    title: string;
    version: number;
    visibility: Visibility;
    updatedAt: string;
    project: OverviewProjectRef;
  }[];
}
